import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { DeploymentVersion, type DeploymentInfo } from "@ensemble/ui";
import { BookOpen, Dumbbell, LockKeyhole, LogOut, Settings, ShieldCheck, X } from "lucide-react";

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? "compact" : ""}`}>
      <span className="brand-mark"><Dumbbell /></span>
      <span className="brand-copy"><small>Ensemble</small><strong>Fitness</strong></span>
    </div>
  );
}

export function ProfileCard({ name }: { name: string }) {
  return (
    <div className="profile-card">
      <span>{name.slice(0, 1)}</span>
      <div><strong>{name}</strong><small>Espace personnel</small></div>
      <LockKeyhole aria-label="Données privées" />
    </div>
  );
}

export function ProfileMenu({ name, email, deployment, onSettings, onDocumentation }: { name: string; email: string; deployment: DeploymentInfo; onSettings: () => void; onDocumentation: () => void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  const run = (action: () => void) => { setOpen(false); action(); };
  return <div className="profile-menu"><button className="profile-trigger" onClick={() => setOpen((value) => !value)} aria-haspopup="menu" aria-expanded={open}><span>{name.slice(0, 1)}</span><div><strong>{name}</strong><small>Espace personnel</small></div><b>•••</b></button>{open && <><button className="profile-dismiss" aria-label="Fermer le menu du profil" onClick={() => setOpen(false)} /><div className="profile-popover" role="menu"><div className="profile-identity"><span>{name.slice(0, 1)}</span><div><strong>{name}</strong><small>{email}</small></div></div><p className="profile-security"><ShieldCheck /> Connecté avec Ensemble Identité</p><button role="menuitem" onClick={() => run(onSettings)}><Settings /> Réglages</button><button role="menuitem" onClick={() => run(onDocumentation)}><BookOpen /> Documentation</button><a role="menuitem" className="profile-logout" href="/api/auth/logout"><LogOut /> Se déconnecter</a><DeploymentVersion {...deployment} /></div></>}</div>;
}

export function ViewHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ReactNode }) {
  return (
    <header className="view-header">
      <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>
      {action}
    </header>
  );
}

export function ProgressRing({ value, label, tone = "gold" }: { value: number; label: string; tone?: "gold" | "sage" }) {
  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;
  return (
    <div className={`progress-ring ${tone}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="ring-track" cx="60" cy="60" r={radius} />
        <circle className="ring-value" cx="60" cy="60" r={radius} strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <strong>{label}</strong>
    </div>
  );
}

export function Sheet({ title, description, onClose, children, mobileFullHeight = false }: { title: string; description?: string; onClose: () => void; children: ReactNode; mobileFullHeight?: boolean }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.body.style.overflow;
    const mainContent = document.querySelector<HTMLElement>(".main-content");
    const previousMainOverflow = mainContent?.style.overflow;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    if (mainContent) mainContent.style.overflow = "hidden";
    window.addEventListener("keydown", close);
    return () => { document.body.style.overflow = previous; if (mainContent) mainContent.style.overflow = previousMainOverflow ?? ""; window.removeEventListener("keydown", close); };
  }, [onClose]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [title]);

  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`sheet ${mobileFullHeight ? "sheet-mobile-full" : ""}`.trim()} role="dialog" aria-modal="true" aria-labelledby="sheet-title">
        <span className="sheet-handle" aria-hidden="true" />
        <header>
          <div><p className="eyebrow">Ensemble Fitness</p><h2 id="sheet-title">{title}</h2>{description && <p>{description}</p>}</div>
          <button className="icon-button" type="button" aria-label="Fermer" onClick={onClose}><X /></button>
        </header>
        <div className="sheet-body" ref={bodyRef}>{children}</div>
      </section>
    </div>,
    document.body,
  );
}

export function SourcePill({ children }: { children: ReactNode }) {
  return <span className="source-pill">{children}</span>;
}

export function formatNumber(value: number, maximumFractionDigits = 2) {
  return value.toLocaleString("fr-FR", { maximumFractionDigits: Math.min(2, Math.max(0, maximumFractionDigits)) });
}

export function shortDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }).format(new Date(`${value}T12:00:00`));
}
